import type { CSSProperties } from "react";
import type { ProgramOverlay } from "./api";

// The full-frame ad break (ADR 0022): an uploaded video clip, or a branded
// sponsor slate. Fills its (positioned) parent; used in the director program
// preview and the viewer stage.

const fill: CSSProperties = { position: "absolute", inset: 0, width: "100%", height: "100%" };

export function AdSurface({ overlay, muted = false }: { overlay: ProgramOverlay; muted?: boolean }) {
  if (overlay.adVideoUrl) {
    return (
      <video
        src={overlay.adVideoUrl}
        autoPlay
        muted={muted}
        playsInline
        loop
        aria-label={`Ad: ${overlay.adName ?? "sponsor"}`}
        style={{ ...fill, objectFit: "cover", background: "#000" }}
      />
    );
  }
  if (overlay.adImageUrl) {
    // An uploaded slate graphic (ADR 0025) — shown full-frame.
    return (
      <img
        src={overlay.adImageUrl}
        alt={`Ad: ${overlay.adName ?? "sponsor"}`}
        style={{ ...fill, objectFit: "contain", background: "#000" }}
      />
    );
  }
  const slate = overlay.adSlate;
  return (
    <div
      aria-label={`Ad: ${slate?.brand ?? overlay.adName ?? "sponsor"}`}
      style={{
        ...fill,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        textAlign: "center",
        padding: 24,
        background: "radial-gradient(120% 120% at 50% 0%, #1f2024 0%, #0a0a0c 70%)",
      }}
    >
      <div style={{ ...monoLabel, color: "var(--text-dim)" }}>
        {slate?.tier || "Sponsored"}
      </div>
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "clamp(28px, 5vw, 52px)",
          fontWeight: 700,
          color: "var(--info-hi)",
          lineHeight: 1.05,
        }}
      >
        {slate?.brand || overlay.adName || "Our Sponsor"}
      </div>
      {slate?.offer && (
        <div style={{ fontSize: "var(--fs-body)", color: "var(--text)", maxWidth: 520 }}>{slate.offer}</div>
      )}
      <div style={{ ...monoLabel, color: "var(--gold)", marginTop: 10 }}>We'll be right back</div>
    </div>
  );
}

const monoLabel: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "var(--fs-2xs)",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "var(--ls-label-wide)",
};
